/* eslint-disable @typescript-eslint/no-explicit-any */
// ─────────────────────────────────────────────────────────────────────
// banepadResolveCourt — "what should THIS court show right now".
//
// Moved verbatim out of apps/web/src/engine/banepad.ts so the API can answer
// the same question server-side. CLAUDE.md names it as one of the shared
// engines precisely because a second implementation would drift: Sentralen, the
// pad preview and the physical pad must agree about the same court, and they
// only do so by running the same code.
//
// Carries the monolith's loose typing. Retyping during a move would mix a
// behaviour risk into a mechanical change — see issue #192.
// ─────────────────────────────────────────────────────────────────────
import { serieMatchCourt, serieMatchDate, timeToMinutes, toMinHHMM } from './time.js';

// Start minute for a match — supports both timeMinutes (tournament) and
// startTime 'HH:MM' (series/booking).
export function banepadMatchStartMin(m: any): number | null {
  if (m == null) return null;
  if (typeof m.timeMinutes === 'number') return m.timeMinutes;
  if (m.startTime) {
    const [h, mm] = String(m.startTime).split(':').map(Number);
    return (h || 0) * 60 + (mm || 0);
  }
  return null;
}

// The court's booking timeline on a single date: previous / ongoing / upcoming.
export function banepadTimelineForCourt(
  court: any,
  bookings: any[],
  nowMin: number,
  todayISO: string,
): any {
  const list = (bookings || [])
    .filter((b) => Number(b?.court) === Number(court) && b?.date === todayISO)
    .map((b) => {
      const s = timeToMinutes(b.startTime || '00:00');
      let e = timeToMinutes(b.endTime || b.startTime || '00:00');
      if (e <= s) e += 1440; // past midnight
      return { ...b, _s: s, _e: e };
    })
    .sort((a, b) => a._s - b._s);
  let current = null,
    prev = null;
  const next = [];
  for (const b of list) {
    if (nowMin >= b._s && nowMin < b._e) {
      if (!current) current = b;
    } else if (b._e <= nowMin)
      prev = b; // sorted → the last one that has passed is the one kept
    else if (b._s > nowMin) next.push(b);
  }
  return { prev, current, next };
}

// Find the ongoing / next / previous match on a court today, generic over
// tournament and series matches (fields: court, date?, timeMinutes|startTime,
// completed, score).
export function banepadFindCourtMatch(
  matches: any[],
  court: any,
  nowMin: number,
  todayISO: string,
  durFallback = 30,
  force = false,
): any {
  // Effective court/date: a series match can carry court/date via its reservation
  // (bookingInfo) instead of a direct field. For tournaments this is identical to
  // m.court/m.date.
  const onCourt = (matches || []).filter((m) => Number(serieMatchCourt(m)) === Number(court));
  // Normally: only matches for today (or without a date). Force mode (the Central
  // screen forces a tournament onto the pads): include all matches on the court
  // regardless of date/time.
  const today = force
    ? onCourt
    : onCourt.filter((m) => {
        const d = serieMatchDate(m);
        return !d || d === todayISO;
      });
  const sorted = today
    .slice()
    .sort((a, b) => (banepadMatchStartMin(a) ?? 99999) - (banepadMatchStartMin(b) ?? 99999));
  let current = null,
    next = null,
    prev = null;
  for (const m of sorted) {
    const s = banepadMatchStartMin(m);
    if (s == null) {
      if (!m.completed && !next) next = m;
      continue;
    }
    const e = s + durFallback;
    if (!m.completed && s <= nowMin && nowMin < e) {
      if (!current) current = m;
    } else if (!m.completed && s > nowMin) {
      if (!next) next = m;
    } else if (m.completed || e <= nowMin) prev = m;
  }
  // Force mode: if the time does not match (all matches have passed or have no
  // time), still show the first unplayed match on the court as "next" — so the
  // pad does not sit empty.
  if (force && !current && !next) next = sorted.find((m) => !m.completed) || null;
  return { current, next, prev };
}

// Decide what is happening on a court RIGHT NOW. Precedence: active tournament →
// active series → ordinary booking → idle. Returns { mode, ... } where mode is
// 'tournament' | 'booking' | 'idle'.

export function banepadResolveCourt({
  court,
  bookings,
  tournament,
  activeSeries,
  activities = [],
  plannedMatches = [],
  nowMin,
  todayISO,
  forcedSessions = null,
  forceTournament = false,
  forceSeries = false,
  forceSingleMatch = null,
}: any): any {
  const forcedOn = forcedSessions && (forcedSessions.size || forcedSessions.length);
  // A local "planned" match (planned_matches_v1: team1/team2 player ids, linked
  // to a booking) is normalised to the SAME activity shape the pad + grid consume,
  // tagged with __planId so the result/live-score handlers route to the plan store.
  const planToActivity = (plan: any) => ({
    id: plan.id,
    __planId: plan.id,
    status: plan.status || 'planned',
    config: {
      tournamentType: 'single_match',
      matchFormat: plan.matchFormat,
      deuceMode: plan.deuceMode,
    },
    registrations: [plan.team1?.p1Id, plan.team1?.p2Id, plan.team2?.p1Id, plan.team2?.p2Id]
      .filter(Boolean)
      .map((pid: any) => ({ playerId: pid, status: 'confirmed' })),
    liveScore: plan.liveScore || null,
  });
  // 0) FORCED single-match (enkeltmatch) from the Central screen → show the
  //    enkeltmatch pad on the court it is booked on, regardless of time. Handles
  //    both an activity (linked via booking.seriesId) and a local plan (linked via
  //    plan.bookingId). Only the linked court is affected; others fall through.
  if (forceSingleMatch && forceSingleMatch.status !== 'cancelled') {
    const isPlan = !!forceSingleMatch.bookingId && !forceSingleMatch.config;
    const linked = isPlan
      ? (bookings || []).find(
          (b) => b.id === forceSingleMatch.bookingId && Number(b.court) === Number(court),
        )
      : (bookings || []).find(
          (b) => b.seriesId === forceSingleMatch.id && Number(b.court) === Number(court),
        );
    if (linked) {
      const ftl = banepadTimelineForCourt(court, bookings, nowMin, todayISO);
      return {
        mode: 'tournament',
        source: 'single_match',
        forced: true,
        formatType: 'single_match',
        name: 'Enkeltmatch',
        activity: isPlan ? planToActivity(forceSingleMatch) : forceSingleMatch,
        booking: linked,
        current: linked,
        next: ftl.next || null,
        prev: ftl.prev || null,
      };
    }
  }
  // 1) Active tournament (Padel24/KM/americano etc.) with a match on the court today.
  //    Skipped when a series is forced (the series should then show on all pads).
  if (!forceSeries && tournament && Array.isArray(tournament.matches)) {
    const dur = tournament.config?.days?.[0]?.matchDuration || 30;
    const r = banepadFindCourtMatch(
      tournament.matches,
      court,
      nowMin,
      todayISO,
      dur,
      forceTournament,
    );
    // The pad only belongs to the tournament when a match is ACTUALLY ON now
    // (r.current), or when it is forced — manually from the Central screen, or by
    // the auto-vis window (which passes forceTournament too). A merely *upcoming*
    // match must NOT take the pad: the court then shows what is really booked.
    // Without this, an americano/mexicano — whose matches carry no start time and
    // therefore always resolve as `next` — hijacked every pad forever, long after
    // the session was over.
    // When FORCE is on we stay on the tournament even with no unplayed match right
    // now (americano only generates the next round once the previous one is done —
    // otherwise the pad would drop to booking the moment the last result is sent).
    if (r.current || forceTournament) {
      return {
        mode: 'tournament',
        source: 'tournament',
        forced: forceTournament,
        formatType: tournament.type || 'padel24',
        name: tournament.name || tournament.config?.name || 'Turnering',
        ...r,
      };
    }
  }
  // 2) Active series (seriespill) with a placed match on the court today. Under
  //    force the series is shown on all pads regardless of date, even when the
  //    court has no match right now.
  if (activeSeries && Array.isArray(activeSeries.matches)) {
    const dur = activeSeries.config?.slotDuration || 90;
    const r = banepadFindCourtMatch(
      activeSeries.matches,
      court,
      nowMin,
      todayISO,
      dur,
      forceSeries,
    );
    // Same rule as the tournament branch above: only an ONGOING match (or force /
    // auto-vis) gives the series the pad — an upcoming match later today does not.
    if (r.current || forceSeries) {
      return {
        mode: 'tournament',
        source: 'series',
        forced: forceSeries,
        formatType: 'seriespill',
        name: activeSeries.config?.name || 'Seriespill',
        ...r,
      };
    }
  }
  // 2.5) Single-match activity (enkeltmatch) linked to the booking on this court
  //      → a playable enkeltmatch pad instead of a plain "booking" card. The
  //      court's current booking carries seriesId = the activity id.
  const tl = banepadTimelineForCourt(court, bookings, nowMin, todayISO);
  if (tl.current?.seriesId && Array.isArray(activities)) {
    const act = activities.find(
      (a) =>
        a.id === tl.current.seriesId &&
        a.config?.tournamentType === 'single_match' &&
        a.status !== 'cancelled',
    );
    if (act) {
      return {
        mode: 'tournament',
        source: 'single_match',
        formatType: 'single_match',
        name: 'Enkeltmatch',
        activity: act,
        booking: tl.current,
        current: tl.current,
        next: tl.next || null,
        prev: tl.prev || null,
      };
    }
  }
  // 2.6) Local "planned" single-match (booker picked the players) linked to this
  //      court's current booking via plan.bookingId → same playable pad.
  if (tl.current?.id && Array.isArray(plannedMatches)) {
    const plan = plannedMatches.find(
      (p) => p.bookingId === tl.current.id && p.status !== 'cancelled' && p.status !== 'completed',
    );
    if (plan) {
      return {
        mode: 'tournament',
        source: 'single_match',
        formatType: 'single_match',
        name: 'Enkeltmatch',
        activity: planToActivity(plan),
        booking: tl.current,
        current: tl.current,
        next: tl.next || null,
        prev: tl.prev || null,
      };
    }
  }
  // 3) Ordinary booking now
  if (tl.current) return { mode: 'booking', ...tl };
  // 3b) FORCED session (force from the Central screen): show the tournament's/session's
  // booking on this court BEFORE its time — so the pads switch to the session's layout early.
  if (forcedOn) {
    const has = (id: any) =>
      forcedSessions.has ? forcedSessions.has(id) : forcedSessions.includes(id);
    const fb = (bookings || [])
      .filter(
        (b) =>
          b.date === todayISO && Number(b.court) === Number(court) && b.seriesId && has(b.seriesId),
      )
      .sort((a, b) => toMinHHMM(a.startTime) - toMinHHMM(b.startTime))[0];
    if (fb)
      return {
        mode: 'booking',
        current: fb,
        next: tl.next || null,
        prev: tl.prev || null,
        forced: true,
      };
  }
  // 4) Idle
  return { mode: 'idle', ...tl };
}
